import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { fetchAuthSession } from '@aws-amplify/auth';
import { spacing } from '../../../design-system/tokens';
import { Input } from '../../../design-system/components/inputs/Input';

interface AccountSetupProps {
  data: { organizationName: string; userName: string } | null;
  onDataChange: (data: { organizationName: string; userName: string }) => void;
}

const Container = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const Title = styled.h2`
  font-size: 32px;
  font-weight: 600;
  color: #222429;
  margin: 0 0 12px 0;
  line-height: 1.25;
  letter-spacing: -0.025em;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0 0 ${spacing[10]} 0;
  line-height: 1.5;
  font-weight: 400;
`;

const FormGroup = styled.div`
  margin-bottom: ${spacing[8]};
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  margin-bottom: 12px;
`;

const extractNameFromCognitoToken = (tokenPayload: any, email: string): string => {
  if (tokenPayload.name?.trim()) {
    return tokenPayload.name.trim();
  }
  
  const nameParts = [tokenPayload.given_name, tokenPayload.family_name].filter(Boolean);
  if (nameParts.length > 0) {
    return nameParts.join(' ');
  }
  
  if (email?.includes('@')) {
    return email.split('@')[0].replace(/[._-]/g, ' ');
  }
  
  return '';
};

export const AccountSetup: React.FC<AccountSetupProps> = ({ data, onDataChange }) => {
  const [formData, setFormData] = useState({
    organizationName: data?.organizationName || '',
    userName: data?.userName || ''
  });

  useEffect(() => {
    if (!data) {
      const loadUserInfo = async () => {
        try {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;
          
          if (idToken) {
            const email = idToken.payload.email as string;
            const name = extractNameFromCognitoToken(idToken.payload, email);
            const newData = { 
              organizationName: '', 
              userName: name || 'User' 
            };
            setFormData(newData);
            onDataChange(newData);
          }
        } catch (error) {
          console.error('Error loading user info:', error);
        }
      };

      loadUserInfo();
    }
  }, [data, onDataChange]);

  const handleInputChange = (field: string, value: string) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    onDataChange(newData);
  };

  return (
    <Container>
      <Title>Welcome</Title>
      <Subtitle>
        Let's start by creating your organization account.
      </Subtitle>

      <FormGroup>
        <Label htmlFor="organizationName">What's your organization name?</Label>
        <Input
          id="organizationName"
          placeholder="Acme Inc"
          value={formData.organizationName}
          onChange={(e) => handleInputChange('organizationName', e.target.value)}
          fullWidth
          required
          style={{ 
            fontSize: '16px', 
            padding: '14px 16px',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            background: '#ffffff',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            boxShadow: 'none'
          }}
        />
      </FormGroup>
    </Container>
  );
};